import { useQuery } from '@apollo/client';
import { AddCircle, Cancel, Devices } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateDeviceCollection } from '~/api/device-collections/create';
import { ClearAllButton, FilterAccordionContainer } from '~/components/Filter';
import { DeviceFilterAccordions, DeviceLogo, DeviceStatus } from '~/components/devices';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { FilterChips } from '~/pages/devices/list/components';
import { DeviceFiltersProvider, useDeviceFilters } from '~/pages/devices/list/context';
import type { DeviceFilter, DeviceFilters } from '~/pages/devices/list/context';
import { DevicePreviewDocument } from './DevicePreview.generated';
import { FilterOptionsDocument } from './FilterOptions.generated';
import { AvailableTargetsDocument } from './TargetAutocomplete.generated';

export interface NewDeviceCollectionDialogProps {
  onClose: () => void;
  onCreate: (collectionId: number) => void;
  open: boolean;
}

interface DialogContentProps {
  loadingOptions: boolean;
  onClose: () => void;
  onCreate: (collectionId: number) => void;
  open: boolean;
}

const getSelectedConditions = (filters: DeviceFilters) => ({
  arch: filters.arch.items.filter((i) => i.selected).map((i) => i.value),
  demo: filters.demo.items.filter((i) => i.selected).map((i) => i.value),
  displayOn: filters.displayOn.items.filter((i) => i.selected).map((i) => i.value),
  displayServers: filters.displayServers.items.filter((i) => i.selected).map((i) => i.value),
  groups: filters.groups.items.filter((i) => i.selected).map((i) => i.value),
  internal: filters.internal.items.filter((i) => i.selected).map((i) => i.value),
  kind: filters.kind.items.filter((i) => i.selected).map((i) => i.value),
  models: filters.models.items.filter((i) => i.selected).map((i) => i.value),
  playerVersions: filters.playerVersions.items.filter((i) => i.selected).map((i) => i.value),
  services: filters.services.items.filter((i) => i.selected).map((i) => i.value),
  silence: filters.silence.items.filter((i) => i.selected).map((i) => i.value),
  status: filters.status.items.filter((i) => i.selected).map((i) => i.value),
  tags: filters.tags.items.filter((i) => i.selected).map((i) => i.value),
  tvBrands: filters.tvBrands.items.filter((i) => i.selected).map((i) => i.value),
});

const NewDeviceCollectionDialogContent = ({
  loadingOptions,
  onClose,
  onCreate,
  open,
}: DialogContentProps) => {
  const { currentNetwork } = useAppContext();
  const { filters, clearFilters } = useDeviceFilters();
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [createDeviceCollection, { loading: creating }] = useCreateDeviceCollection();
  const { t } = useTranslation();

  const hasSelectedFilters = useMemo(
    () =>
      (Object.values(filters) as DeviceFilter<unknown>[]).some((filter) =>
        filter.items.some((item) => item.selected),
      ),
    [filters],
  );

  const conditions = useMemo(
    () => ({ ...getSelectedConditions(filters), networkId: currentNetwork.id }),
    [filters, currentNetwork.id],
  );

  const { data: previewData, loading: loadingPreview } = useQuery(DevicePreviewDocument, {
    variables: {
      networkId: currentNetwork.id,
      condition: conditions,
    },
    skip: !open,
  });

  const showPreview = hasSelectedFilters;
  const deviceCount = showPreview ? previewData?.network?.devices.pageInfo.nodesCount ?? 0 : 0;
  const devices = showPreview ? previewData?.network?.devices.nodes ?? [] : [];
  const filterCounts = previewData?.network?.deviceFilterInfo;

  const handleClose = useCallback(() => {
    onClose();
    setName('');
    setError(null);
    clearFilters();
  }, [onClose, clearFilters]);

  const handleSave = async () => {
    if (!name.trim()) return;

    setError(null);
    try {
      const result = await createDeviceCollection({
        variables: {
          input: { conditions, name: name.trim(), source: 'MANUAL' },
        },
        refetchQueries: [
          { query: AvailableTargetsDocument, variables: { networkId: currentNetwork.id } },
        ],
      });

      if (result.data?.createDeviceCollection?.deviceCollection) {
        onCreate(result.data.createDeviceCollection.deviceCollection.id);
        handleClose();
      } else if (result.errors?.length) {
        setError(result.errors[0].message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('api.common.error_generic'));
    }
  };

  const canSave = name.trim().length > 0 && hasSelectedFilters;

  return (
    <Dialog maxWidth="md" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>
        {t('pages.device_signals.new_filter_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <TextField
            autoFocus
            fullWidth
            label={t('pages.device_signals.new_filter_dialog.name_label')}
            placeholder={t('pages.device_signals.new_filter_dialog.name_placeholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FilterChips />
              <ClearAllButton
                onClick={clearFilters}
                startIcon={<Cancel />}
                disabled={!hasSelectedFilters}
              >
                {t('common.filter.clear_all')}
              </ClearAllButton>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, minHeight: 300 }}>
            {/* Filters Column */}
            <Box sx={{ flex: 1, maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {loadingOptions ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 200,
                    }}
                  >
                    <CircularProgress aria-label={t('common.status.loading')} />
                  </Box>
                ) : (
                  <FilterAccordionContainer sx={{ padding: 0, marginRight: 0 }}>
                    <DeviceFilterAccordions filterCounts={filterCounts} />
                  </FilterAccordionContainer>
                )}
              </Box>
            </Box>

            {/* Device Preview Column */}
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                maxHeight: 400,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'grey.50',
                }}
              >
                <Devices color="action" />
                <Typography variant="subtitle2">
                  {t('pages.device_signals.new_filter_dialog.matching_devices')}
                </Typography>
                {loadingPreview ? (
                  <CircularProgress
                    aria-label={t('common.status.loading')}
                    size={16}
                    sx={{ ml: 'auto' }}
                  />
                ) : (
                  <Chip
                    label={t('pages.device_signals.settings.target_groups.device_count_plural', {
                      count: deviceCount,
                    })}
                    size="small"
                    color={deviceCount > 0 ? 'primary' : 'default'}
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>
              {!hasSelectedFilters ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('pages.device_signals.new_filter_dialog.select_filters_prompt')}
                  </Typography>
                </Box>
              ) : devices.length === 0 && !loadingPreview ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('pages.device_signals.new_filter_dialog.no_devices_match')}
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                  {devices.map((device) => (
                    <ListItem key={device.id} divider>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                        <DeviceLogo kind={device.kind} />
                      </Box>
                      <ListItemText
                        primary={device.name}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <DeviceStatus device={device} />
                    </ListItem>
                  ))}
                  {deviceCount > devices.length && (
                    <ListItem>
                      <ListItemText
                        primary={t('pages.device_signals.new_filter_dialog.and_more', {
                          count: deviceCount - devices.length,
                        })}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={creating}>
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          color="primary"
          disabled={!canSave}
          loading={creating}
          startIcon={<AddCircle />}
          variant="contained"
          onClick={handleSave}
        >
          {t('pages.device_signals.new_filter_dialog.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const NewDeviceCollectionDialog = ({
  onClose,
  onCreate,
  open,
}: NewDeviceCollectionDialogProps) => {
  const { currentNetwork } = useAppContext();

  const { data: filterOptionsData, loading: loadingOptions } = useQuery(FilterOptionsDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !open,
  });

  const network = filterOptionsData?.network;

  return (
    <DeviceFiltersProvider
      deviceGroups={network?.allDeviceGroups}
      deviceTags={network?.deviceTags.map((tag) => tag.name)}
      displayServers={network?.displayServers.map((d) => d)}
      models={network?.models.map((m) => m)}
      playerVersions={network?.playerVersions.map((v) => v)}
      tvBrands={network?.allTvBrands}
    >
      <NewDeviceCollectionDialogContent
        loadingOptions={loadingOptions}
        onClose={onClose}
        onCreate={onCreate}
        open={open}
      />
    </DeviceFiltersProvider>
  );
};
